// WIRE types for the app-facing platform endpoints that serve manifest data:
//
//   GET  /apps/_api/slots/:slotId/assignments
//   POST /apps/_api/apps/:appId/workflows/:workflowId/run
//   POST /apps/_app/:appName/call/:handler (the URL only: the answer is the handler's own)
//
// They live beside the manifest schema because they are the same contract seen
// from the other side: the manifest is what an app DECLARES, these are what the
// product frontend RECEIVES. A consumer of one always needs the other, so they
// ship together. Types only — the routes are in src/serve/apps-platform-api.ts.
//
// CASING: fields the PLATFORM computes are snake_case, matching the sibling
// /apps/_api routes. The `widget` field is the app's own manifest fragment and
// keeps the manifest's camelCase — re-casing it would rewrite an
// author-controlled document (and `paramsSchema.properties` carries the app's
// own field names, which must survive byte-for-byte).

import type { NavigationEntry, SlotId, Widget } from './manifest';

// ---- GET /apps/_api/slots/:slotId/assignments -------------------------------

// Which stored version of an app the assignments response resolves. Mirrors
// lumi-mcp-server's own `AppChannel`, and travels as the `channel` query param on
// the route above — the ONE place in the system where the channel is a request
// parameter. Every request the browser makes afterwards reaches the origin this
// response names and takes its channel from that host instead.
export type AppChannel = 'release' | 'draft';

// Enough to attribute a widget to its app and label it in a UI. Deliberately NOT
// the whole manifest: a slot query should not leak every other widget and
// workflow the app declares.
export interface AssignedApp {
    // The manifest's own id — opaque, conventionally `<publisher>/<app>`. NOT a
    // URL component: see the note on `bundle_path` below.
    id: string;
    name: string;
    description: string;
    // The app's declared Navigation (spec 2026-09-16), passed through VERBATIM
    // in the manifest's own casing when the live manifest declares one. ABSENT
    // otherwise, and then the host derives the rail entries from the full-page
    // routes — absence is the grandfathered state, not an empty tree. Per-APP:
    // every assignment of the same app carries the same value. An entry's asset
    // icon resolves against the assignment's `assets_base_url`.
    navigation?: NavigationEntry;
}

// One (app, widget) pair filling the queried slot.
export interface SlotAssignmentView {
    app: AssignedApp;
    // The authored widget declaration, passed through VERBATIM. A discriminated
    // union on `kind`, so a consumer narrowing on it gets `route` exactly where
    // it exists.
    widget: Widget;
    // SUPERSEDED by `bundle_url`, and still sent on every response. A path
    // relative to the PRODUCT origin, which is where it now 404s: widget
    // documents moved to the apps origin, which a path alone cannot express.
    // Never use it — a fallback to it is a request to the wrong host.
    bundle_path: string;
    // The ABSOLUTE iframe `src` on this response's `apps_origin` — the field to
    // use. Per-widget, and a sibling of `widget` rather than a field inside it, so
    // the verbatim manifest fragment stays free of platform-computed values: a
    // consumer can always tell what the author declared from what we derived.
    //
    // Optional for the same reason `apps_origin` is: absent when the request host
    // has no derived apps host, and then the widget is simply not addressable and
    // must be skipped.
    bundle_url?: string;
    // The ABSOLUTE URL for running one of this app's declared workflows, with a
    // literal `{workflowId}` left for the caller to substitute (URL-encoded). On
    // this response's `apps_origin`, so a widget's POST is same-origin and its
    // `__Host-apps_sess` cookie authenticates the call.
    //
    // Per-APP, not per-widget: both widgets of one app carry the same value. Served
    // rather than derived because the run route's shape is a server decision — the
    // product passes it into the frame verbatim (the `slot.token` message) and the
    // SDK only substitutes the placeholder, so moving the route stays a server-side
    // change.
    //
    // Optional and absent exactly when `bundle_url` is, for the same reason. Its
    // absence does not stop a FRAMED widget rendering — that widget simply has no
    // workflow surface. A `workflow` button cannot run without it, so the host
    // withholds one; `navigate` and `call` buttons never read it.
    workflow_run_url_template?: string;
    // The ABSOLUTE base of this app's bundle files on this response's
    // `apps_origin`, trailing slash included:
    // `https://<tenant>-apps.luminovo.com/apps/<name>/`. The ONE thing a consumer
    // may do with it is append a bundle-relative path the manifest declares —
    // today a navigation icon's `assets/catalog/<file>` — so the host, its
    // channel suffix and the serving prefix stay server decisions. Per-app,
    // duplicated per assignment. Optional and absent exactly when `bundle_url` is.
    assets_base_url?: string;
}

export interface SlotAssignmentsResponse {
    // Echoes the queried slot, so a response is interpretable on its own.
    slot: SlotId;
    // The tenant's paired apps host, as a bare origin — scheme, host, optional
    // port, no path and no trailing slash. Served here, and NOT derivable by the
    // client, for two reasons: it is the same value the product must POST
    // /apps/_session to before any widget document will load, and it is the
    // value an incoming `event.origin` is compared against on the postMessage
    // bridge. A client that assembled it from its own hostname would be
    // pinning the bridge to a string it invented, which is no check at all.
    //
    // OPTIONAL, and absent on more deployments than not: the platform derives it
    // from the request Host through its trusted index, which admits only
    // `*.luminovo.com` hosts one label below the apex. A `.dev.luminovo.ai`
    // environment, a staging host, or an unwired index all yield nothing here —
    // the documented degradation being that no widget is addressable and none
    // renders.
    apps_origin?: string;
    // Empty when no widget declares the slot — a normal state, not an error.
    // Ordered: registry position across apps, then declaration order within one.
    assignments: SlotAssignmentView[];
}

// The 400 body for a slot id outside SLOT_IDS. The route's other failure modes
// (401/403/429) are the shared /apps surface shapes, not specific to it.
export interface SlotAssignmentsError {
    error: 'invalid_request';
    message: string;
}

// The literal token inside `workflow_run_url_template` that a consumer replaces
// with a URL-ENCODED workflow id. Mirrors WORKFLOW_ID_PLACEHOLDER in
// lumi-mcp-server's src/apps/manifest.ts, which builds the template.
//
// Lives here rather than in the SDK because both sides must agree on it: the
// server writes it, every vendored SDK copy substitutes it. Braces cannot appear
// in a manifest id, so it can never collide with a real path segment.
export const WORKFLOW_ID_PLACEHOLDER = '{workflowId}';

// ---- POST /apps/_api/apps/:appId/workflows/:workflowId/run -----------------

// Request body. `input` is the workflow's arguments, shaped by the declaring
// workflow's own paramsSchema. Optional: a workflow whose schema requires
// nothing is invoked with no body at all.
export interface WorkflowRunRequest {
    input?: Record<string, unknown>;
}

// Echoes what was addressed, so a response is interpretable on its own and a
// client logging it need not correlate back to the request URL. Present on every
// outcome below, including the not-yet-implemented one.
export interface WorkflowRef {
    app_id: string;
    workflow_id: string;
    // The declared display name, so a UI can say which workflow it ran without a
    // second lookup.
    name: string;
}

// The 501 body served while execution is unwired. NOT a permanent shape: when
// the run path lands this is replaced by the real result, and a client should
// treat 501 as "this deployment cannot run workflows yet" rather than a failure
// of the specific workflow.
export interface WorkflowRunNotImplemented {
    error: 'not_implemented';
    message: string;
    workflow: WorkflowRef;
}

// The 404 body when the app is unknown OR declares no such workflow. The two are
// deliberately not distinguished — both mean "this app does not offer that
// workflow", and splitting them would let a caller enumerate which app ids exist.
export interface WorkflowRunUnknown {
    error: 'unknown_workflow';
    message: string;
}

// ---- POST /apps/_app/:appName/call/:handler ---------------------------------

// Where a `call` button sends its click: one handler in the app's worker.js, on
// the response's `apps_origin`, so a draft assignment reaches the draft backend.
// Built by the caller rather than served, unlike `workflow_run_url_template`:
// every widget with a worker.js already calls this path by name, so it cannot
// move, and a button's handler is fixed in its manifest.
//
// `appId` is the assignment's `app.id`, the manifest id. The route matches the
// app NAME; the platform makes the two equal at upload and leaves out of the
// assignments a `call` button whose app breaks that. The body is the handler's
// `input` as it is, and the answer is whatever JSON the handler returns.
export function callUrl(appsOrigin: string, appId: string, handler: string): string {
    return `${appsOrigin}/apps/_app/${encodeURIComponent(appId)}/call/${encodeURIComponent(handler)}`;
}
